import { useEffect } from "react";

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy | Truestays";
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", "How Truestays collects, uses and protects your personal data in line with UK GDPR.");
    setMeta("robots", "index, follow");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://www.truestays.co.uk/privacy-policy";
  }, []);

  return (
    <div style={{ background: "#F7F7F7", minHeight: "100vh" }}>
      {/* Hero */}
      <div style={{ background: "#112215", padding: "100px clamp(20px, 5vw, 80px) 56px", textAlign: "center" }}>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: "#4A6DC7", marginBottom: 12, marginTop: 0 }}>Legal</p>
        <h1 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: "clamp(28px, 4vw, 48px)", color: "#fff", margin: "0 0 16px", letterSpacing: "-0.03em", lineHeight: 1.08 }}>Privacy Policy</h1>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#888", margin: "0 auto", maxWidth: 480 }}>Last updated: June 2025</p>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "56px clamp(20px, 5vw, 40px) 80px" }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: "40px clamp(24px, 5vw, 56px)", border: "1px solid #E5E5E5" }}>

          <Section title="1. Who we are">
            <p>Truestays Ltd ("Truestays", "we", "us", "our") is a short-let property management company registered in England and Wales. Our registered office is in Manchester, United Kingdom.</p>
            <p>We are the data controller for the personal information we collect through this website and our services. If you have any questions about how we handle your data, contact us at <a href="mailto:hello@truestays.co.uk" style={{ color: "#4A6DC7", textDecoration: "none" }}>hello@truestays.co.uk</a>.</p>
          </Section>

          <Section title="2. What information we collect">
            <p>We collect information you provide directly to us, including:</p>
            <ul>
              <li>Name, email address, and phone number (via enquiry and estimate forms)</li>
              <li>Property address, postcode, and details (bedrooms, type, condition)</li>
              <li>Photos or listing links you choose to share with us</li>
              <li>Messages and correspondence sent through our website or by email</li>
            </ul>
            <p>We also collect information automatically when you visit our website:</p>
            <ul>
              <li>Browser type, operating system, and device information</li>
              <li>Pages visited, time on site, and referring URLs</li>
              <li>IP address (anonymised where possible)</li>
            </ul>
          </Section>

          <Section title="3. How we use your information">
            <p>We use your personal information to:</p>
            <ul>
              <li>Respond to your enquiries and provide income estimates</li>
              <li>Onboard and manage your property under our management service</li>
              <li>Send you updates, service communications, and marketing (where you have consented)</li>
              <li>Improve our website and services</li>
              <li>Comply with our legal obligations</li>
            </ul>
          </Section>

          <Section title="4. Legal basis for processing">
            <p>We process your personal data under the following legal bases:</p>
            <ul>
              <li><strong>Contract:</strong> to fulfil our property management agreement with you</li>
              <li><strong>Legitimate interests:</strong> to respond to enquiries and improve our services</li>
              <li><strong>Consent:</strong> for marketing communications and referrals to partner companies</li>
              <li><strong>Legal obligation:</strong> to comply with applicable laws and regulations</li>
            </ul>
          </Section>

          <Section title="5. Sharing your information">
            <p>We do not sell your personal data. We may share it with:</p>
            <ul>
              <li><strong>Service providers:</strong> including Airbnb, Booking.com, and other platforms to list and manage your property</li>
              <li><strong>Technology partners:</strong> including Houst (our technology and guest care partner) under appropriate data processing agreements</li>
              <li><strong>Referral partners:</strong> only where you have given explicit consent for us to refer you to another local property management company</li>
              <li><strong>Professional advisers:</strong> accountants, lawyers, and insurers where necessary</li>
              <li><strong>Regulators and authorities:</strong> where required by law</li>
            </ul>
          </Section>

          <Section title="6. Data retention">
            <p>We retain your personal data for as long as necessary to provide our services and comply with legal obligations. Typically:</p>
            <ul>
              <li>Enquiry data: up to 2 years if no contract is formed</li>
              <li>Client and contract data: 7 years after the end of the management agreement</li>
              <li>Marketing data: until you unsubscribe or withdraw consent</li>
            </ul>
          </Section>

          <Section title="7. Cookies">
            <p>Our website uses cookies to improve your experience and analyse traffic. These include essential cookies required for the site to function, and analytics cookies (such as Google Analytics) to help us understand how visitors use the site.</p>
            <p>You can manage cookie preferences through your browser settings at any time.</p>
          </Section>

          <Section title="8. Your rights">
            <p>Under UK GDPR, you have the right to:</p>
            <ul>
              <li>Access the personal data we hold about you</li>
              <li>Correct inaccurate data</li>
              <li>Request erasure of your data</li>
              <li>Object to or restrict processing</li>
              <li>Data portability</li>
              <li>Withdraw consent at any time (without affecting prior processing)</li>
            </ul>
            <p>To exercise any of these rights, email <a href="mailto:hello@truestays.co.uk" style={{ color: "#4A6DC7", textDecoration: "none" }}>hello@truestays.co.uk</a>. We will respond within 30 days.</p>
          </Section>

          <Section title="9. Security">
            <p>We take reasonable technical and organisational measures to protect your personal data against unauthorised access, loss, or misuse. However, no method of transmission over the internet is 100% secure.</p>
          </Section>

          <Section title="10. Changes to this policy">
            <p>We may update this Privacy Policy from time to time. The latest version will always be available on this page. We encourage you to review it periodically.</p>
          </Section>

          <Section title="11. Contact us" last>
            <p>For any privacy-related questions or to exercise your rights, contact us at:</p>
            <p>
              <strong>Truestays Ltd</strong><br />
              <a href="mailto:hello@truestays.co.uk" style={{ color: "#4A6DC7", textDecoration: "none" }}>hello@truestays.co.uk</a><br />
              0161 850 6950
            </p>
            <p>You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noreferrer" style={{ color: "#4A6DC7", textDecoration: "none" }}>ico.org.uk</a>.</p>
          </Section>

        </div>
      </div>
    </div>
  );
}

function Section({ title, children, last }) {
  return (
    <div style={{ marginBottom: last ? 0 : 36, paddingBottom: last ? 0 : 36, borderBottom: last ? "none" : "1px solid #F0F0F0" }}>
      <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 18, color: "#1A1A1A", marginBottom: 14, marginTop: 0 }}>{title}</h2>
      <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#4A4A4A", lineHeight: 1.75 }}>
        {children}
      </div>
    </div>
  );
}